import { useChat, type UIMessage } from '@ai-sdk/react'
import { lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai'
import { DefaultChatTransport } from 'ai'
import { OpenAPI } from 'openapi'
import type { CreativeChatBodyDto } from 'openapi/models/CreativeChatBodyDto'
import { type RefObject, useEffect, useMemo, useRef } from 'react'
import { ulid } from 'ulid'
import { API_BASE } from '../utils/chat-markers'

/** The closed set of creative-chat agent variants (generated from the server enum). */
export type CreativeChatIntent = NonNullable<CreativeChatBodyDto['intent']>

export interface UseCreativeChatOptions {
  /** Agent variant for the shared `/v1/creative/chat` agent. */
  intent: CreativeChatIntent
  /** useChat id — distinct per surface so threads don't collide. */
  chatId: string
  /** Auto-send a hidden seed on mount to make the agent open first. */
  seedOnMount: boolean
  /** Hidden seed text sent on mount when seedOnMount — triggers the agent's opener. */
  seedMessage: string
  /** Reports an unrecoverable agent error so the parent can react (fallback / toast). */
  onAgentError?: () => void
  /**
   * Persist the conversation server-side (non-ephemeral) so it survives reopen.
   * The hidden seed is excluded from the durable thread. Defaults to false.
   */
  persist?: boolean
  /** Hydrated history to seed useChat with (e.g. from GET /brand-chat). */
  initialMessages?: UIMessage[]
  /**
   * Ids of the photos to attach to the NEXT outgoing message. Read (per-request)
   * by the transport body; the owning component sets `.current` right before send.
   */
  attachmentIdsRef?: RefObject<string[]>
}

/**
 * The shared engine behind every creative-chat surface: builds the per-request
 * transport (auth header + intent + ephemeral/attachment body), wires `useChat`,
 * and auto-sends the hidden seed once on mount so the agent opens first. Callers
 * own their own rendering and send semantics — this hook only owns the transport,
 * the chat state, and the seed/error effects that were copy-pasted across the
 * chat components.
 */
export function useCreativeChat({
  intent,
  chatId,
  seedOnMount,
  seedMessage,
  onAgentError,
  persist = false,
  initialMessages,
  attachmentIdsRef,
}: UseCreativeChatOptions) {
  // Stable id for the hidden seed so the server can exclude it from the durable
  // thread (kept only long enough to greet). Generated once per mount.
  const seedMessageIdRef = useRef<string>(ulid())

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/v1/creative/chat`,
        headers: () => ({
          ...(OpenAPI.TOKEN ? { Authorization: `Bearer ${OpenAPI.TOKEN}` } : {}),
        }),
        body: () => ({
          intent,
          creatorMode: 'instagram',
          ephemeral: !persist,
          ...(persist ? { seedMessageId: seedMessageIdRef.current } : {}),
          ...(attachmentIdsRef && attachmentIdsRef.current.length > 0
            ? { attachmentIds: attachmentIdsRef.current }
            : {}),
        }),
      }),
    [intent, persist, attachmentIdsRef]
  )

  const { messages, sendMessage, addToolApprovalResponse, status, error } = useChat({
    id: chatId,
    transport,
    generateId: ulid,
    // HITL: answering an approval card resubmits automatically so the paused
    // tool executes (or is denied) without an extra user send.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    ...(initialMessages?.length ? { messages: initialMessages } : {}),
  })

  // Surface unrecoverable agent errors so the parent can fall back to forms.
  useEffect(() => {
    if (error) onAgentError?.()
  }, [error, onAgentError])

  // Auto-send a hidden seed once on mount so the agent opens with the first turn.
  const seedSentRef = useRef(false)
  useEffect(() => {
    if (!seedOnMount || seedSentRef.current || messages.length > 0) return
    seedSentRef.current = true
    // Send the seed as a NEW user message carrying our stable id. We must set
    // `id` here — NOT `messageId`: in AI SDK v6 the `messageId` option is the
    // "edit an existing message" API, which findIndex()es the id and THROWS
    // "message with id … not found" for an id that isn't already in the thread.
    // On a fresh chat that throw aborted the send, so the agent never opened.
    // The stable id lets the server exclude the seed from the durable thread.
    void sendMessage({
      id: seedMessageIdRef.current,
      role: 'user',
      parts: [{ type: 'text', text: seedMessage }],
    })
    // Depend on seedOnMount so a chat mounted early with seedOnMount=false (e.g.
    // onboarding renders the chat before the business exists) still seeds the
    // moment it flips true. seedSentRef + the messages guard keep this single-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedOnMount])

  const isLoading = status === 'streaming' || status === 'submitted'

  return {
    messages,
    sendMessage,
    addToolApprovalResponse,
    isLoading,
    seedMessageId: seedMessageIdRef.current,
  }
}
